/**
 * Text normalization + typo-tolerant answer matching.
 * Ported from v1's public/core/utils.js. The semantics are part of the product:
 * a one-character slip shouldn't cost the user their streak, and "the dog" /
 * "o cachorro" / "dog" should all count as the same answer.
 */

const LEADING_ARTICLE_REGEX = /^(the|a|an|o|os|as)\s+/i;
/**
 * English infinitive marker. Verb glosses read "to work", "to go" — and a user
 * typing "work" is right. Without this it is the same failure as comparing
 * "entrada" against "entrada (ingresso)": a correct answer three edits away
 * from the stored string. Portuguese needs no equivalent; its infinitive is one
 * word ("trabalhar").
 */
const LEADING_TO_REGEX = /^to\s+/i;

/** Lowercase, trim, strip a leading article, collapse internal whitespace. */
export function normalizeInput(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(LEADING_ARTICLE_REGEX, '')
    .replace(LEADING_TO_REGEX, '')
    .replace(/\s+/g, ' ');
}

/** Levenshtein edit distance between two strings. */
export function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;

  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 0; i <= n; i++) dp[i][0] = i;
  for (let j = 0; j <= m; j++) dp[0][j] = j;

  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j - 1] + 1, // substitution
          dp[i][j - 1] + 1, // insertion
          dp[i - 1][j] + 1, // deletion
        );
      }
    }
  }
  return dp[n][m];
}

/**
 * The forms a single gloss can legitimately be typed as.
 *
 * Glosses carry a parenthetical to disambiguate a sense — "nota (escolar)",
 * "star (celebrity)", "ponto (de ônibus)". The parenthetical is context, not
 * part of the answer, so a user typing "nota" is right and was being marked
 * wrong: the whole string was compared, and "nota" is eleven edits from
 * "nota (escolar)". 286 glosses in the deck carry one.
 *
 * Both readings are accepted, because both are things a person actually types:
 * the head word alone ("ponto") and the phrase with the brackets dropped
 * ("ponto de ônibus"). The bracketed text on its own is deliberately NOT a
 * candidate — "escolar" must not pass for `Note`, nor "mulher" for `Chefin`.
 */
export function glossVariants(candidate: string): string[] {
  const variants = new Set([candidate]);
  if (candidate.includes('(')) {
    variants.add(candidate.replace(/[()]/g, ' '));
    variants.add(candidate.replace(/\s*\([^)]*\)/g, ' '));
  }
  return [...variants].map((v) => v.replace(/\s+/g, ' ').trim()).filter(Boolean);
}

/** Accepts an input if it matches any candidate exactly OR within Levenshtein ≤ 1. */
export function isCorrectAnswer(input: string, candidates: string[]): boolean {
  if (!input.trim() || candidates.length === 0) return false;
  const norm = normalizeInput(input);
  return candidates.some((candidate) =>
    glossVariants(candidate).some((variant) => {
      const normCand = normalizeInput(variant);
      if (norm === normCand) return true;
      return levenshtein(norm, normCand) <= 1;
    }),
  );
}
